import axios from 'axios'
import { isQrPasswordValid, normalizeQrPassword } from 'features/shared/client'

import { isAuthenticatedAtom } from '@/global-state'
import { getStore } from '@/store'

import { qrTypeAtom } from '../../generatorState'
import { GENERATOR_SEARCH_PARAM, QR_TYPE } from '../../generatorTypes'
import { qrCodesApi } from '../../services/shared'
import { getGeneratorSearchParam } from '../../utils/generatorSearchParam'
import {
	clearGuestDraftPassword,
	readGuestDraftPassword,
	writeGuestDraftPassword,
} from './guestDraftPasswordStorage'
import {
	draftPasswordAtom,
	passwordAtom,
	passwordEnabledAtom,
	passwordRepeatAtom,
	savedPasswordAtom,
} from './passwordDesignState'
import { loadSavedQrPassword, writeQrPassword } from './qrPasswordUtils'

export const loadDraftQrPassword = async (
	qrId: string,
	isAuthenticated: boolean,
) => {
	if (isAuthenticated) {
		// The draft was claimed, so the server copy is the source of truth
		clearGuestDraftPassword()
		return loadSavedQrPassword(qrId)
	}
	const kept = readGuestDraftPassword(qrId)
	if (kept === null) {
		// The browser copy is the only way a guest can read a password back, so
		// one that can't be shown is dropped rather than published unseen
		try {
			await qrCodesApi.deleteGuestQrPassword(qrId)
		} catch (error) {
			if (
				!(axios.isAxiosError(error) && error.response?.status === 404)
			) {
				throw error
			}
		}
	}
	return kept
}

// Edit mode drafts made by a plain POST (Change QR type, the step 2 Save) start
// without a password, and step 3 follows the draft, so the live code's would
// read as removed. Throws when it can't copy, so no draft is handed to step 3
// that would publish a removal.
export const seedDraftPasswordFromTarget = async (
	draftId: string,
	qrType: QR_TYPE | null | undefined,
) => {
	const targetId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Target)
	if (!targetId || qrType === QR_TYPE.Wifi) {
		return
	}
	const password = await loadSavedQrPassword(targetId)
	if (password !== null) {
		await qrCodesApi.putQrPassword(draftId, password)
	}
}

// undefined means there is nothing to save yet: Wi-Fi has no password, and an
// incomplete pair is not a password the user has chosen
export const getDraftPasswordTarget = ({
	isEnabled,
	password,
	repeat,
	qrType,
}: {
	isEnabled: boolean
	password: string
	repeat: string
	qrType: QR_TYPE | null | undefined
}): string | null | undefined => {
	if (qrType === QR_TYPE.Wifi) {
		return undefined
	}
	if (!isEnabled) {
		return null
	}
	return isQrPasswordValid(password, repeat)
		? normalizeQrPassword(password)
		: undefined
}

export const saveDraftQrPassword = async () => {
	const store = getStore()
	const draftId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)
	const isAuthenticated = store.get(isAuthenticatedAtom)
	const draft = store.get(draftPasswordAtom)
	// Until the draft is read, an untouched form would wipe what is stored
	if (
		!draftId ||
		draft?.qrId !== draftId ||
		draft.isAuthenticated !== isAuthenticated
	) {
		return
	}
	const target = getDraftPasswordTarget({
		isEnabled: store.get(passwordEnabledAtom),
		password: store.get(passwordAtom),
		repeat: store.get(passwordRepeatAtom),
		qrType: store.get(qrTypeAtom),
	})
	if (target === undefined || target === draft.password) {
		return
	}
	await writeQrPassword(draftId, target, isAuthenticated)
	store.set(draftPasswordAtom, { ...draft, password: target })
	if (!getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Target)) {
		store.set(savedPasswordAtom, target)
	}
	if (!isAuthenticated) {
		if (target === null) {
			clearGuestDraftPassword()
		} else {
			writeGuestDraftPassword(draftId, target)
		}
	}
}
